import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '@/store/gameStore';
import { listPuzzles, getPuzzle } from '@/engine/loader';
import { isPuzzleUnlocked, isChapterUnlocked } from '@/engine/progression';
import type { PuzzleMeta } from '@/engine/loader';

const chapters = [
  { number: 1, name: 'Awakening' },
  { number: 2, name: 'Intervals' },
  { number: 3, name: 'Current' },
  { number: 4, name: 'Reflection' },
  { number: 5, name: 'Threshold' },
  { number: 6, name: 'Cascade' },
  { number: 7, name: 'Synthesis' },
] as const;

export function PuzzleSelect() {
  const navigateTo = useGameStore((s) => s.navigateTo);
  const loadPuzzle = useGameStore((s) => s.loadPuzzle);
  const loadPuzzlePractice = useGameStore((s) => s.loadPuzzlePractice);
  const currentChapter = useGameStore((s) => s.currentChapter);
  const setChapter = useGameStore((s) => s.setChapter);
  const progress = useGameStore((s) => s.progress);
  const isPracticeSelect = useGameStore((s) => s.isPracticeSelect);
  const [puzzles, setPuzzles] = useState<PuzzleMeta[]>([]);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const list = listPuzzles(currentChapter);
    setPuzzles(list);
  }, [currentChapter]);

  const handlePuzzleClick = useCallback(
    (meta: PuzzleMeta) => {
      // In practice mode, all puzzles are unlocked
      if (!isPracticeSelect) {
        const unlocked = isPuzzleUnlocked(progress, meta.id, meta.chapter, meta.index);
        if (!unlocked) return;
      }

      const puzzle = getPuzzle(meta.id);
      if (puzzle) {
        if (isPracticeSelect) {
          loadPuzzlePractice(puzzle);
        } else {
          loadPuzzle(puzzle);
        }
      }
    },
    [loadPuzzle, loadPuzzlePractice, progress, isPracticeSelect],
  );

  const handleChapterClick = useCallback(
    (chapterNum: number) => {
      // In practice mode, all chapters are unlocked
      if (!isPracticeSelect && !isChapterUnlocked(progress, chapterNum)) return;
      setChapter(chapterNum);
    },
    [setChapter, progress, isPracticeSelect],
  );

  const getGridColumns = () => {
    if (windowWidth < 480) return 2;
    if (windowWidth < 768) return 3;
    return 5;
  };

  const columns = getGridColumns();
  const chapterInfo = chapters.find((c) => c.number === currentChapter);

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button
          style={backButtonStyle}
          aria-label="Back to main menu"
          onClick={() => {
            // Clear practice select flag when going back
            if (isPracticeSelect) {
              useGameStore.setState({ isPracticeSelect: false });
            }
            navigateTo('menu');
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }}
        >
          &#8592;
        </button>
        <h2 style={headerTitleStyle}>
          {isPracticeSelect ? 'Practice — ' : ''}Chapter {currentChapter}: {chapterInfo?.name ?? ''}
        </h2>
      </div>

      {/* Chapter tabs */}
      <div style={tabContainerStyle}>
        {chapters.map((ch) => {
          const isActive = currentChapter === ch.number;
          const isHovered = hoveredTab === ch.number;
          const chapterOpen = isPracticeSelect || isChapterUnlocked(progress, ch.number);
          return (
            <button
              key={ch.number}
              style={{
                ...tabStyle,
                ...(isActive ? tabActiveStyle : {}),
                ...(isHovered && !isActive && chapterOpen ? tabHoverStyle : {}),
                ...(!chapterOpen ? tabLockedStyle : {}),
              }}
              onClick={() => handleChapterClick(ch.number)}
              onMouseEnter={() => setHoveredTab(ch.number)}
              onMouseLeave={() => setHoveredTab(null)}
              disabled={!chapterOpen}
            >
              {chapterOpen ? '' : '\uD83D\uDD12 '}Ch.{ch.number} {ch.name}
            </button>
          );
        })}
      </div>

      {/* Puzzle grid */}
      <div
        style={{
          ...gridStyle,
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
        }}
      >
        {puzzles.length === 0 && (
          <div style={emptyStateStyle}>
            No puzzles available yet for this chapter.
          </div>
        )}
        {puzzles.map((meta) => {
          const isHovered = hoveredCard === meta.id;
          const unlocked = isPracticeSelect || isPuzzleUnlocked(progress, meta.id, meta.chapter, meta.index);
          const savedStars = isPracticeSelect ? 0 : (progress.journey.stars[meta.id] ?? 0);

          return (
            <button
              key={meta.id}
              style={{
                ...cardStyle,
                ...(unlocked && isHovered ? cardHoverStyle : {}),
                ...(!unlocked ? cardLockedStyle : {}),
              }}
              onClick={() => handlePuzzleClick(meta)}
              onMouseEnter={() => setHoveredCard(meta.id)}
              onMouseLeave={() => setHoveredCard(null)}
              disabled={!unlocked}
              aria-label={unlocked
                ? `Puzzle ${meta.index}, ${meta.title}, Tier ${meta.tier}, ${savedStars} stars`
                : `Puzzle ${meta.index}, ${meta.title}, locked`}
            >
              {unlocked ? (
                <>
                  <span style={cardNumberStyle}>{meta.index}</span>
                  <span style={cardTitleStyle}>{meta.title}</span>
                  <span style={cardTierStyle}>Tier {meta.tier}</span>
                  <span style={cardStarsStyle}>{renderStars(savedStars)}</span>
                </>
              ) : (
                <>
                  <span style={lockIconStyle}>{'\uD83D\uDD12'}</span>
                  <span style={cardTitleLockedStyle}>{meta.title}</span>
                  <span style={cardTierStyle}>Tier {meta.tier}</span>
                  <span style={cardStarsStyle}>{renderStars(0)}</span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function renderStars(count: number): string {
  const filled = '\u2605'; // filled star
  const empty = '\u2606';  // empty star
  return filled.repeat(count) + empty.repeat(3 - count);
}

// ── Styles ──

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#0A0A1A',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 24px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  flexShrink: 0,
};

const backButtonStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#e8e8e4',
  fontSize: '20px',
  cursor: 'pointer',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  transition: 'background 0.2s ease',
  flexShrink: 0,
  outline: 'none',
};

const headerTitleStyle: React.CSSProperties = {
  fontSize: 'clamp(16px, 3vw, 22px)',
  fontWeight: 300,
  color: '#e8e8e4',
  letterSpacing: '0.08em',
  margin: 0,
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const tabContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  padding: '12px 24px',
  flexShrink: 0,
};

const tabStyle: React.CSSProperties = {
  padding: '8px 20px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
  color: 'rgba(232,232,228,0.5)',
  fontSize: '13px',
  fontWeight: 400,
  cursor: 'pointer',
  letterSpacing: '0.04em',
  transition: 'all 0.2s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
};

const tabActiveStyle: React.CSSProperties = {
  background: 'rgba(114, 9, 183, 0.2)',
  borderColor: 'rgba(114, 9, 183, 0.4)',
  color: '#e8e8e4',
};

const tabHoverStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  borderColor: 'rgba(255,255,255,0.15)',
};

const tabLockedStyle: React.CSSProperties = {
  opacity: 0.35,
  cursor: 'not-allowed',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: '12px',
  padding: '16px 24px',
  overflowY: 'auto',
  flex: 1,
  alignContent: 'start',
};

const emptyStateStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  textAlign: 'center',
  padding: '60px 24px',
  color: 'rgba(232,232,228,0.3)',
  fontSize: '15px',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  padding: '20px 12px 16px',
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  color: '#e8e8e4',
};

const cardHoverStyle: React.CSSProperties = {
  background: 'rgba(114, 9, 183, 0.12)',
  borderColor: 'rgba(114, 9, 183, 0.35)',
  boxShadow: '0 4px 20px rgba(114, 9, 183, 0.12)',
  transform: 'translateY(-2px)',
};

const cardLockedStyle: React.CSSProperties = {
  opacity: 0.35,
  cursor: 'not-allowed',
  filter: 'grayscale(0.5)',
};

const cardNumberStyle: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 200,
  color: 'rgba(199, 125, 255, 0.8)',
  lineHeight: 1,
};

const lockIconStyle: React.CSSProperties = {
  fontSize: '24px',
  lineHeight: 1,
  opacity: 0.5,
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 400,
  color: 'rgba(232,232,228,0.7)',
  textAlign: 'center',
  lineHeight: 1.3,
  minHeight: '32px',
  display: 'flex',
  alignItems: 'center',
};

const cardTitleLockedStyle: React.CSSProperties = {
  ...cardTitleStyle,
  color: 'rgba(232,232,228,0.35)',
};

const cardTierStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 400,
  color: 'rgba(232,232,228,0.3)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const cardStarsStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(252, 191, 73, 0.6)',
  letterSpacing: '2px',
};
